import React, { useEffect, useRef, useState } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import {
  addArea,
  deleteArea,
  fetchAreaCoordinates,
  getAreasList,
  importAreas,
  updateArea,
} from 'utils/api/ares'
import AreaRow from './areaRow'
import DataTableImportButton from 'components/common/dataTable/dataTableImportButton'
import { ImportColumn } from 'components/common/importModal'
import { getImportTemplate, downloadImportSample } from 'utils/api/importApi'
import ExportAreasButton from './exportAreasButton'
import toast from 'react-hot-toast'
import { PlusIcon } from 'components/icons'
import { Button } from 'components/common'
import useModal from 'hooks/useModal'
import CreateAreaConfigModal from './createAreaModal'
import { yupResolver } from '@hookform/resolvers/yup'
import { areasFormSchema } from 'utils/schema/areasSchema'

export type ListType = {
  _id: string
  shortCode: string
  fullName: string
  areaLat?: number | null
  areaLong?: number | null
}

const AreasList = ({ isEditor }: { isEditor: boolean }) => {
  const [loading, setLoading] = useState<boolean>(false)
  const [coordLoading, setCoordLoading] = useState(false)
  const [areaImportColumns, setAreaImportColumns] = useState<ImportColumn[]>([])

  const { closeModal, isOpen, openModal } = useModal()

  const {
    control,
    register,
    getValues,
    reset,
    formState: { errors },
    handleSubmit,
  } = useForm<{
    areas: ListType[]
  }>({
    resolver: yupResolver(areasFormSchema),
    defaultValues: {
      areas: [],
    },
    mode: 'onBlur',
  })

  const originalDataRef = useRef<Record<string, ListType>>({})

  const { fields, replace, remove } = useFieldArray({
    control,
    name: 'areas',
  })

  const handleSaveData = (data: ListType[]) => {
    replace(data)
    const originalMap: Record<string, ListType> = {}
    data.forEach((item) => {
      originalMap[item._id] = item
    })
    originalDataRef.current = originalMap
  }

  const handleGetAreasList = async () => {
    try {
      const { data } = await getAreasList()
      if (data && Array.isArray(data)) {
        handleSaveData(data)
      }
    } catch (error) {
      console.error('Error fetching areas:', error)
    }
  }

  const handleRowSave = async (index: number, id: string) => {
    try {
      const currentRow = getValues(`areas.${index}`)
      const originalRow = originalDataRef.current[id]

      if (
        currentRow.fullName !== originalRow.fullName ||
        currentRow.shortCode !== originalRow.shortCode
      ) {
        const updatedRow: ListType = {
          _id: originalRow._id,
          fullName: currentRow.fullName,
          shortCode: currentRow.shortCode,
        }
        setLoading(true)
        await updateArea(updatedRow)

        toast.success('Area updated successfully', {
          duration: 3000,
        })
      } else {
        console.log('No changes detected for row', index)
      }
    } catch (error: any) {
      if (error?.response?.status === 409) {
        toast.error('shortCode is already exist', {
          duration: 3000,
        })
      }
      if (error?.response?.status === 422) {
        console.log(error)
        toast.error('Invalid Syntax', {
          duration: 3000,
        })
      }
    } finally {
      setLoading(false)
      const { data } = await getAreasList()
      if (data && Array.isArray(data)) {
        handleSaveData(data)
      }
      reset({ areas: data })
    }
  }

  const fetchCoords = async () => {
    const loadingToast = toast.loading('Fetching coordinates...', {
      duration: Infinity,
    })
    setCoordLoading(true)

    try {
      await fetchAreaCoordinates()
      await handleGetAreasList()
      toast.success('Coordinates fetched successfully', {
        id: loadingToast,
        duration: 3000,
      })
    } catch (error) {
      toast.error('Failed to fetch coordinates', {
        id: loadingToast,
        duration: 3000,
      })
    } finally {
      setCoordLoading(false)
    }
  }

  useEffect(() => {
    handleGetAreasList()
  }, [])

  useEffect(() => {
    getImportTemplate('areas')
      .then((res: { data: { columns: ImportColumn[] } }) =>
        setAreaImportColumns(res.data.columns),
      )
      .catch(() => {})
  }, [])

  const handleDownloadAreasSample = async () => {
    const response = await downloadImportSample('areas')
    const url = URL.createObjectURL(
      new Blob([response.data], { type: 'text/csv' }),
    )
    const link = document.createElement('a')
    link.href = url
    link.download = 'areas_sample.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="mx-auto  max-w-5xl p-6 ">
      <div className="mb-2 flex items-center justify-end gap-2">
        {isEditor && (
          <Button
            type="button"
            variant="outline"
            buttonText="Fetch Coordinates"
            loading={coordLoading}
            onClick={fetchCoords}
          />
        )}
        <CreateAreaConfigModal
          closeModal={closeModal}
          isOpen={isOpen}
          onSuccess={async (data, reset) => {
            try {
              await addArea(data)
              const { data: newData } = await getAreasList()
              if (newData && Array.isArray(newData)) {
                handleSaveData(newData)
                toast.success(`New Area Mapped Created`, {
                  duration: 3000,
                })
              }
            } catch (error: any) {
              if (error?.response?.status === 409) {
                toast.error('shortCode is already exist', {
                  duration: 3000,
                })
              }
            } finally {
              closeModal()
              reset()
            }
          }}
        />
        <Button
          disabled={!isEditor}
          type="button"
          buttonText="Add"
          variant="custom"
          iconClassName="!h-4 !w-4 text-dataTableIcon"
          icon={<PlusIcon />}
          className="rounded-lg px-3.5 py-2 text-sm font-semibold text-sidebar-primary hover:bg-gray-200"
          onClick={() => {
            openModal()
          }}
        />
        {isEditor && importAreas && (
          <DataTableImportButton
            disabled={loading}
            uploadApiFunction={importAreas}
            importColumns={areaImportColumns}
            importModuleName="areas"
            onDownloadSample={handleDownloadAreasSample}
          />
        )}

        <ExportAreasButton />
      </div>
      <div className="relative mb-4 grid grid-cols-5 gap-4 rounded-lg bg-gray-200 px-4 py-2 font-semibold text-gray-800">
        <div className="sticky">Short Code</div>
        <div className="sticky">Full Name</div>
        <div className="sticky">Latitude</div>
        <div className="sticky">Longitude</div>
        <div className="sticky top-2">Action</div>
      </div>

      {fields.map((field, index) => {
        return (
          <AreaRow
            key={field._id}
            field={field}
            index={index}
            isEditor={isEditor}
            control={control}
            loading={loading}
            errors={errors}
            register={register}
            handleSubmit={handleSubmit}
            handleRowSave={handleRowSave}
            handleDelete={(idx, id) => {
              remove(idx)
              deleteArea(id)
              toast.success(`Area Mapped Deleted Successfully`, {
                duration: 3000,
              })
            }}
            originalData={originalDataRef.current[field._id]}
          />
        )
      })}
    </div>
  )
}

export default AreasList
